
import ResetPasswordComponent from "./ResetPasswordComponent.vue";
import { ref } from "vue";
import ResetPasswordSuccess from "./ResetPasswordSuccess.vue";
import NewPassword from "./NewPassword.vue";
import ResetOptions from "./ResetOptions.vue";
import { UserData } from "../../../../domain/entities/entities";
import { authenticationUseCases } from "../../../../domain/usecase/impl/authenticationUseCases";
import store from "../../../repository/store/store";
import router from "@/routes";
import {
  dispatchErrorBox,
  enableBodyOverflow,
  getObjectByValue
} from "../mixins";
import { RepositoryException } from "../../../../domain/exceptions/exceptions";

export default {
  name: "ResetPassword",
  components: {
    ResetOptions,
    NewPassword,
    ResetPasswordSuccess,
    ResetPasswordComponent
  },

  setup() {
    const emailHasBeenSent = ref(false);

    /** Get user's information and parse it to a specific format to make a request and validate the user's credentials
     * @param userData Contains all data that the backend needs to validate the user's credentials, like document, name, etc... */
    const formattedUserData = (userData: UserData): UserData => {
      const formattedData: UserData = getObjectByValue(userData);

      /** Remove all characters except numbers */
      if (formattedData.document) {
        formattedData.document = formattedData.document.replace(
          /[^a-zA-Z0-9]/g,
          ""
        );
      }

      /** Parse to number attributes that should be numbers but is string */
      if (formattedData.badgeCode) {
        formattedData.badgeCode = Number(formattedData.badgeCode);
      }

      if (formattedData.name) {
        formattedData.name = formattedData.name.trim();
      }

      if (formattedData.mothersName) {
        formattedData.mothersName = formattedData.mothersName.trim();
      }

      return formattedData;
    };

    /** Send user's information to validate their credentials and if it matches, the user will be able to reset their password
     * @param userData Contains all data that the backend needs to validate the user's credentials, like document, name, etc... */
    const checkUserCredentials = async (userData: UserData): Promise<void> => {
      try {
        const userDataToSend = formattedUserData(userData);
        const token = await authenticationUseCases.checkUserCredentials(
          userDataToSend
        );
        await store.dispatch("miscellany_module/setSuccess", {
          message: "Usuário validado com sucesso!",
          changed: true
        });
        await router.push({
          name: "home",
          params: { resetPass: "reset-password", resetBy: "token", token }
        });
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      } finally {
        enableBodyOverflow();
      }
    };

    async function recoverPasswordByDocument(userData: UserData) {
      try {
        const userDataToSend = formattedUserData(userData);
        const response = await authenticationUseCases.recoverPassword(
          userDataToSend.document
        );
        if (response.recoveryType === "data") {
          await router.push({
            name: "home",
            params: { resetPass: "reset-password", resetBy: "identity" }
          });
        } else {
          emailHasBeenSent.value = true;
        }
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    async function recoverPasswordByEmail(userData: UserData) {
      try {
        const response = await authenticationUseCases.recoverPasswordByEmail(
          userData.document
        );
        if (response.recoveryType === "data") {
          await router.push({
            name: "home",
            params: { resetPass: "reset-password", resetBy: "identity" }
          });
        } else {
          emailHasBeenSent.value = true;
        }
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    return {
      emailHasBeenSent,
      checkUserCredentials,
      recoverPasswordByDocument,
      recoverPasswordByEmail
    };
  }
};
